import { formatPrice, isNative, isWeb } from "@stepapp/core";
import {
  Block,
  Button,
  LinePlaceholder,
  palette,
  SvgIcon,
  spacing,
  Touchable,
  Typography,
  useBreakpoints,
} from "@stepapp/ui";
import React from "react";
import { useTranslation } from "react-i18next";
import { Platform, StyleSheet } from "react-native";
import { AppStorage } from "../services/AppStorage";

type Props = {
  disabled?: boolean;
  label?: string;
  loading?: boolean;
  onPress: () => void;
  price: number | string;
  showDetails?: boolean;
  handleShowDetails: () => void;
};

const ServiceFooter = ({
  disabled,
  loading,
  price,
  onPress,
  label,
  showDetails = false,
  handleShowDetails,
}: Props) => {
  const { t } = useTranslation();
  const { xl } = useBreakpoints();
  const priceFormatter = formatPrice(AppStorage().get("countryCode"));

  const formattedPrice = typeof price === "string" ? price : priceFormatter(price);
  const showPriceOrDetails = !!formattedPrice || showDetails;

  return (
    <Block
      id="serviceFooter"
      justifyEnd
      row
      paddingHorizontal={isWeb ? 2.5 : 0}
      paddingBottom={2}
      paddingTop={0.5}
      style={styles.content}
    >
      {showPriceOrDetails && (
        <Block justifyCenter flex={2} marginLeft={isNative ? 2.5 : 0}>
          {loading ? (
            <LinePlaceholder height={3.5} width={12} />
          ) : (
            <Typography variant="newH2">{formattedPrice}</Typography>
          )}

          {showDetails && (
            <Touchable onPress={handleShowDetails}>
              <Block alignEnd row>
                <Block marginRight={1}>
                  <SvgIcon color={palette.font.secondary} name="info" size={24} />
                </Block>
                <Typography decoration="underline" variant="paragraph">
                  {t("common.details")}
                </Typography>
              </Block>
            </Touchable>
          )}
        </Block>
      )}

      <Block
        flex={3}
        marginLeft={showPriceOrDetails ? 0 : 2.5}
        marginRight={isNative ? 2.5 : 0}
        style={xl && styles.fullPagePaddingRight}
      >
        <Button
          disabled={disabled}
          label={label || t("screens.service.nextDataAddress")}
          loading={loading}
          onPress={onPress}
          type="primary"
          variant="default"
        />
      </Block>
    </Block>
  );
};

export default React.memo(ServiceFooter);

const styles = StyleSheet.create({
  content: {
    backgroundColor: palette.common.white,
    borderTopColor: palette.common.lightGray,
    borderTopLeftRadius: spacing.radius,
    borderTopRightRadius: spacing.radius,
    borderTopWidth: spacing.border,
    bottom: 0,
    boxShadow: "0px -4px 20px rgba(255, 255, 255, 1)",
    left: 0,
    margin: "auto",
    maxWidth: 1180,
    right: 0,
    width: "100%",
    zIndex: 200,
    ...Platform.select({
      default: {
        position: "absolute",
      },
      web: {
        // biome-ignore lint/suspicious/noExplicitAny: web styles
        position: "fixed" as any,
      },
    }),
  },
  fullPagePaddingRight: {
    marginRight: spacing.unit * 7,
    paddingRight: `${100 / 3}%`,
  },
});
